/**
 * Opt-in lookup for a cryptic bank line. Does not run until asked — every
 * categorize tap must not spend an API call.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchTransactionInsight, type TransactionInsight } from '../api/client';
import { Button } from './ui';
import { ChatIcon, SearchIcon } from './icons';

interface AskAboutChargeProps {
  transactionId: string;
  onFile: (budgetItemId: string) => void;
  busy?: boolean;
}

export function AskAboutCharge({ transactionId, onFile, busy }: AskAboutChargeProps) {
  const [state, setState] = useState<'idle' | 'loading' | 'error' | 'ready'>('idle');
  const [insight, setInsight] = useState<TransactionInsight | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function lookup() {
    setState('loading');
    setError(null);
    try {
      const result = await fetchTransactionInsight(transactionId);
      setInsight(result);
      setState('ready');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Couldn't look this up. Try again.");
      setState('error');
    }
  }

  if (state === 'idle') {
    return (
      <div className="insight">
        <Button size="sm" emphasis="ghost" onClick={() => void lookup()}>
          <SearchIcon width={16} height={16} />
          Ask about this
        </Button>
      </div>
    );
  }

  if (state === 'loading') {
    return (
      <div className="insight" aria-busy="true">
        <p className="insight__status">Looking this up…</p>
      </div>
    );
  }

  if (state === 'error') {
    return (
      <div className="insight" role="alert">
        <p className="insight__status">{error}</p>
        <Button size="sm" onClick={() => void lookup()}>
          Try again
        </Button>
      </div>
    );
  }

  if (!insight) return null;

  const canFile = Boolean(insight.suggestedBudgetItemId && insight.suggestedBudgetItemName);
  const chatHref = `/chat?autosend=1&prefillTransactionId=${encodeURIComponent(transactionId)}&q=${encodeURIComponent(
    investigationPrompt({
      title: insight.merchant || 'this charge',
      remoteId: transactionId,
    })
  )}`;

  return (
    <div className="insight">
      <p className="insight__summary">{insight.summary}</p>
      {insight.relatedTo && (
        <p className="insight__meta">
          Related to <strong>{insight.relatedTo}</strong>
        </p>
      )}
      {canFile && insight.suggestedBudgetItemId && insight.suggestedBudgetItemName && (
        <Button
          size="sm"
          emphasis="primary"
          disabled={busy}
          onClick={() => onFile(insight.suggestedBudgetItemId as string)}
        >
          {insight.confidence >= 0.45
            ? `File under ${insight.suggestedBudgetItemName}`
            : `File under ${insight.suggestedBudgetItemName} anyway`}
        </Button>
      )}
      <Link to={chatHref} className="btn btn--ghost btn--sm">
        <ChatIcon width={16} height={16} />
        Continue in Chat
      </Link>
      {insight.reasons.length > 0 && (
        <ul className="insight__reasons">
          {insight.reasons.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function investigationPrompt(options: {
  title: string;
  amount?: string;
  date?: string;
  rawDescription?: string;
  note?: string;
  remoteId?: string;
}): string {
  const lines = [
    'Investigate this bank charge. Hunt it down — do not only check whether we already filed something similar.',
    '',
    'Decode abbreviations and processor prefixes, search the web for the raw descriptor, and tell me what it likely is. If an envelope or bill fits, propose filing it so I can confirm in one tap.',
    '',
    `Charge: ${options.title}`,
  ];
  if (options.amount) lines.push(`Amount: ${options.amount}`);
  if (options.date) lines.push(`Date: ${options.date}`);
  if (options.rawDescription && options.rawDescription !== options.title) {
    lines.push(`Bank line: ${options.rawDescription}`);
  }
  if (options.note) lines.push(`Note: ${options.note}`);
  if (options.remoteId) lines.push(`id: ${options.remoteId}`);
  return lines.join('\n');
}
